import { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { ApiKey } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ErrorNote } from '../components/ErrorNote';
import { Loading } from '../components/Loading';
import { ReadEdit } from '../components/ReadEdit';
import { SecretOnce } from '../components/SecretOnce';
import { whenLabel } from '../format';

/**
 * The keys a publisher's own systems use to pull subscribers.
 *
 * Two things on this screen are not reversible and both are said out loud rather than implied. A
 * key is shown once, because only its hash is stored and there is nothing left to show afterwards.
 * Revoking one takes effect immediately, and what it breaks is somebody's import job, so it asks
 * first and names the consequence rather than saying "are you sure".
 *
 * Reading the list is open to the whole team: an editor wondering why a feed stopped should be
 * able to see that a key was revoked last week. Creating and revoking are an owner's, because a
 * key reads every subscriber this publisher has.
 */
export function ApiKeys() {
  const { org } = useAuth();
  const orgId = org?.org_id ?? null;
  const orgName = org?.name ?? 'this publisher';
  const isOwner = org?.role === 'owner';

  const [keys, setKeys] = useState<readonly ApiKey[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [created, setCreated] = useState<{ name: string; key: string } | null>(null);

  const load = useCallback(() => {
    if (orgId === null) return;
    setProblem(null);
    setKeys(null);
    api
      .apiKeys(orgId)
      .then((body) => setKeys(body.api_keys))
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  const live = (keys ?? []).filter((key) => key.revoked_at === null);
  const revoked = (keys ?? []).filter((key) => key.revoked_at !== null);

  return (
    <>
      <Title order={1} size="h2">
        API keys
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        A key lets one of your own systems read{' '}
        <Anchor component={Link} to="/newsletters">
          {orgName}'s subscribers
        </Anchor>
        , server to server. Send it as <Code>Authorization: Bearer</Code> on{' '}
        <Code>GET /v1/subscribers</Code>, which is paged and takes the same filters as the
        subscribers screen.
      </Text>

      {!isOwner && (
        <Text c="dimmed" size="sm" mb="md">
          Only an owner can create or revoke a key, because a key reads every subscriber this
          publisher has. Ask one of the owners of {orgName} if you need one.
        </Text>
      )}

      {created !== null && (
        <SecretOnce
          title={`The key for ${created.name}`}
          value={created.key}
          instruction="Paste it into whatever is going to read your subscribers. We store only a hash of it, so nobody here can look it up later: if it is lost, the only way back is to create another one and revoke this one."
          onDismiss={() => setCreated(null)}
        />
      )}

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && keys === null && <Loading label="Loading your keys" />}

      {keys !== null && (
        <>
          {/*
           * Said out loud rather than left to be discovered. A key exists here as a name and two
           * dates: the value itself was shown once and only its hash was kept, so there is nothing
           * to reveal on this screen and nothing that should look like it could be.
           */}
          <Text size="sm" c="dimmed" mt="sm">
            A key's value is shown once, when it is made. What is kept here is its name, when it was
            made and when it was last used.
          </Text>

          {live.length === 0 ? (
            <Text c="dimmed" mt="sm">
              No keys yet. Nothing can pull your subscribers until one is made.
            </Text>
          ) : (
            <Stack
              component="ul"
              gap="xs"
              mt="sm"
              style={{ listStyle: 'none', margin: 0, padding: 0 }}
            >
              {live.map((key) => (
                <KeyRow
                  key={key.key_id}
                  apiKey={key}
                  orgId={orgId ?? ''}
                  canRevoke={isOwner}
                  onRevoked={load}
                />
              ))}
            </Stack>
          )}

          {revoked.length > 0 && (
            <>
              <Title order={2} size="h3" mt="xl">
                Revoked
              </Title>
              <Text size="sm" c="dimmed" mt={4}>
                Kept in the list so that a feed which stopped working has an explanation on screen.
              </Text>
              <Stack
                component="ul"
                gap="xs"
                mt="sm"
                style={{ listStyle: 'none', margin: 0, padding: 0 }}
              >
                {revoked.map((key) => (
                  <KeyRow
                    key={key.key_id}
                    apiKey={key}
                    orgId={orgId ?? ''}
                    canRevoke={false}
                    onRevoked={load}
                  />
                ))}
              </Stack>
            </>
          )}
        </>
      )}

      {isOwner && (
        <CreateKeyForm
          orgId={orgId ?? ''}
          onCreated={(name, key) => {
            setCreated({ name, key });
            load();
          }}
        />
      )}
    </>
  );
}

function KeyRow({
  apiKey,
  orgId,
  canRevoke,
  onRevoked,
}: {
  apiKey: ApiKey;
  orgId: string;
  canRevoke: boolean;
  onRevoked: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function revoke() {
    setProblem(null);
    setBusy(true);
    try {
      await api.revokeApiKey(orgId, apiKey.key_id);
      notifications.show({ message: `${apiKey.name} revoked.`, color: 'violet' });
      onRevoked();
    } catch (error) {
      setProblem(errorMessage(error));
      setBusy(false);
      setConfirming(false);
    }
  }

  return (
    <Card component="li" withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
        <div>
          <Group gap="xs" align="center">
            <Text fw={500}>{apiKey.name}</Text>
            {apiKey.revoked_at !== null && (
              <Badge size="sm" radius="sm" variant="light" color="gray" tt="none">
                Revoked
              </Badge>
            )}
          </Group>
          <Text size="sm" c="dimmed" mt={2}>
            Created {whenLabel(apiKey.created_at)}
            {apiKey.revoked_at !== null && `, revoked ${whenLabel(apiKey.revoked_at)}`}
          </Text>
          <Text size="sm" c="dimmed">
            {apiKey.last_used_at === null
              ? 'Never used'
              : `Last used ${whenLabel(apiKey.last_used_at)}`}
          </Text>
        </div>
        {canRevoke && !confirming && (
          <Button
            variant="outline"
            color="red"
            size="compact-sm"
            onClick={() => setConfirming(true)}
            aria-label={`Revoke ${apiKey.name}`}
          >
            Revoke
          </Button>
        )}
      </Group>

      {problem !== null && <ErrorNote message={problem} />}

      {confirming && (
        <Card withBorder radius="md" p="md" mt="md">
          <Text size="sm" fw={500}>
            Revoke {apiKey.name}?
          </Text>
          <Text size="sm" mt={4}>
            It stops working the moment you press this, not at the end of the day. Anything still
            using it, usually somebody's import job, starts failing straight away. If you are
            replacing it, make the new key first, put it in, and revoke this one afterwards.
          </Text>
          <Group mt="sm">
            <Button
              color="red"
              size="compact-sm"
              disabled={busy}
              onClick={() => void revoke()}
              autoFocus
            >
              {busy ? 'Revoking...' : `Yes, revoke ${apiKey.name}`}
            </Button>
            <Button
              variant="default"
              size="compact-sm"
              onClick={() => setConfirming(false)}
              disabled={busy}
            >
              Keep it
            </Button>
          </Group>
        </Card>
      )}
    </Card>
  );
}

/**
 * Making a key, behind a press.
 *
 * The press matters more here than on the other screens: what comes back is shown once and cannot
 * be looked up afterwards, so a form somebody opens the screen into is a form somebody can create
 * a key from without having decided to.
 */
function CreateKeyForm({
  orgId,
  onCreated,
}: {
  orgId: string;
  onCreated: (name: string, key: string) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);

  async function create(name: string): Promise<boolean> {
    setProblem(null);
    try {
      const body = await api.createApiKey(orgId, name.trim());
      onCreated(body.api_key.name, body.key);
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  return (
    <Card component="section" withBorder radius="lg" p="lg" mt="xl">
      <Title order={2} size="h3">
        Create a key
      </Title>
      <Text size="sm" c="dimmed" mt={4}>
        The key is shown once, on this screen, and never again. Have somewhere to paste it ready
        before you press the button.
      </Text>
      {problem !== null && <ErrorNote message={problem} />}

      <ReadEdit<string>
        noun="the new key"
        canEdit
        asForm
        open={() => ''}
        openLabel="Create a key"
        openName="Create a key"
        saveLabel="Create key"
        saveName="Create key"
        busyLabel="Creating..."
        problemsTitle="Before a key can be made:"
        check={(name) =>
          name.trim() === ''
            ? ['Give the key a name, so anybody can tell later what it is for.']
            : []
        }
        save={create}
        form={(name, set) => (
          <TextInput
            id="api-key-name"
            label="What is it for"
            description="Name the system that will use it, such as our Mailchimp sync, so it is obvious later which one to turn off."
            maxLength={120}
            mt="md"
            value={name}
            onChange={(event) => set(event.currentTarget.value)}
            style={{ maxWidth: '24rem' }}
          />
        )}
      />
    </Card>
  );
}
